import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AudioDayDto, AudioRecordingDto, AudioSegmentDto } from '@geotracker/contracts';
import { api, apiBlob } from '@/lib/api/client';
import { formatClock, formatDuration, formatLag } from '@/lib/format';

/**
 * Voice recordings, for one employee's day.
 *
 * The design decision that makes this fast: the amplitude envelope and the
 * audio are fetched separately. The day query returns kilobytes of `peaks`, so
 * every waveform draws the instant the tab opens; the audio itself — hundreds of
 * kilobytes a minute — is fetched a segment at a time, and only for the
 * recording actually being listened to. Scrubbing a waveform is free because it
 * is scrubbing a number array the browser already holds.
 */

export function useAudioDay(employeeId: number | null, date: string) {
  return useQuery({
    queryKey: ['audio', employeeId, date],
    queryFn: () =>
      api.get<AudioDayDto>(`/admin/employees/${employeeId}/audio${date ? `?date=${date}` : ''}`),
    enabled: employeeId !== null,
    select: (r) => r.data,
  });
}

interface AudioTabProps {
  employeeId: number;
  date: string;
}

export function AudioTab({ employeeId, date }: AudioTabProps) {
  const query = useAudioDay(employeeId, date);

  if (query.isPending) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Loading recordings…</p>;
  }
  if (query.error) {
    return (
      <p className="py-6 text-center text-sm text-danger">
        Could not load recordings. {query.error instanceof Error ? query.error.message : ''}
      </p>
    );
  }

  const day = query.data;
  if (!day || day.recordings.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border py-6 text-center">
        <p className="text-sm font-medium">No recordings on this day</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
          Voice capture runs only while an administrator has it switched on, and only on a device
          where the employee has consented and granted the microphone. Android shows its own
          recording indicator the whole time — nothing here is captured silently.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Recordings" value={String(day.summary.recordingCount)} />
        <Stat label="Total audio" value={formatDuration(day.summary.totalSeconds)} />
        <Stat label="Segments" value={String(day.summary.segmentCount)} />
      </div>

      <ol className="space-y-3">
        {day.recordings.map((recording) => (
          <li key={recording.id}>
            <RecordingPlayer recording={recording} timezone={day.timezone} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-2.5">
      <div className="text-lg font-semibold tabular-nums leading-tight">{value}</div>
      <div className="text-2xs text-muted-foreground">{label}</div>
    </div>
  );
}

/**
 * One recording: a waveform, a playhead, and sequential segment playback.
 *
 * Playback walks the segments in order through a single hidden `<audio>`
 * element, swapping its source as each finishes. The waveform is the whole
 * recording's overview envelope; the playhead is the global position across all
 * segments, so to the supervisor it is one clip from 14:05 to 14:12, not twelve
 * files.
 */
function RecordingPlayer({
  recording,
  timezone,
}: {
  recording: AudioRecordingDto;
  timezone: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [loadingSegment, setLoadingSegment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Segment start offsets within the recording, so a global position maps to a
  // segment and an offset inside it.
  const offsets = useMemo(() => {
    const starts: number[] = [];
    let acc = 0;
    for (const s of recording.segments) {
      starts.push(acc);
      acc += s.durationMs;
    }
    return { starts, total: acc };
  }, [recording.segments]);

  // Object URLs for fetched segments, cached for the life of the component and
  // revoked on unmount so a day of listening does not leak blobs.
  const urlCache = useRef(new Map<number, string>());
  useEffect(() => {
    const cache = urlCache.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  const segmentUrl = useCallback(async (segment: AudioSegmentDto): Promise<string> => {
    const cached = urlCache.current.get(segment.id);
    if (cached) return cached;
    const blob = await apiBlob(segment.streamUrl);
    const url = URL.createObjectURL(blob);
    urlCache.current.set(segment.id, url);
    return url;
  }, []);

  // Which segment the current global position falls in.
  const activeIndex = useMemo(() => {
    let index = 0;
    for (let i = 0; i < offsets.starts.length; i++) {
      if (elapsedMs >= (offsets.starts[i] ?? 0)) index = i;
      else break;
    }
    return index;
  }, [elapsedMs, offsets.starts]);

  /** Loads a segment into the audio element at an offset, optionally playing. */
  const loadSegment = useCallback(
    async (index: number, offsetMs: number, play: boolean) => {
      const audio = audioRef.current;
      const segment = recording.segments[index];
      if (!audio || !segment) return;
      setError(null);
      setLoadingSegment(true);
      try {
        const url = await segmentUrl(segment);
        // Only swap the source if it changed — re-setting src reloads and stalls.
        if (audio.src !== url) audio.src = url;
        await new Promise<void>((resolve) => {
          if (audio.readyState >= 1) return resolve();
          audio.addEventListener('loadedmetadata', () => resolve(), { once: true });
        });
        audio.currentTime = Math.max(0, offsetMs) / 1000;
        if (play) await audio.play();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Playback failed');
        setPlaying(false);
      } finally {
        setLoadingSegment(false);
      }
    },
    [recording.segments, segmentUrl],
  );

  // Prefetch the next segment's audio while the current one plays, so the seam
  // between segments is as small as the network allows.
  useEffect(() => {
    if (!playing) return;
    const next = recording.segments[activeIndex + 1];
    if (next) void segmentUrl(next).catch(() => undefined);
  }, [playing, activeIndex, recording.segments, segmentUrl]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    const startAt = elapsedMs >= offsets.total ? 0 : elapsedMs;
    if (elapsedMs >= offsets.total) setElapsedMs(0);
    const idx = indexFor(startAt, offsets.starts);
    void loadSegment(idx, startAt - (offsets.starts[idx] ?? 0), true);
  }, [playing, elapsedMs, offsets, loadSegment]);

  const seek = useCallback(
    (ms: number) => {
      const clamped = Math.max(0, Math.min(offsets.total, ms));
      setElapsedMs(clamped);
      const idx = indexFor(clamped, offsets.starts);
      void loadSegment(idx, clamped - (offsets.starts[idx] ?? 0), playing);
    },
    [offsets, loadSegment, playing],
  );

  // Audio element event wiring: advance the global clock, and step to the next
  // segment when one ends.
  const onTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setElapsedMs((offsets.starts[activeIndex] ?? 0) + audio.currentTime * 1000);
  }, [offsets.starts, activeIndex]);

  const onEnded = useCallback(() => {
    const nextIndex = activeIndex + 1;
    if (nextIndex < recording.segments.length) {
      setElapsedMs(offsets.starts[nextIndex] ?? 0);
      void loadSegment(nextIndex, 0, true);
    } else {
      setPlaying(false);
      setElapsedMs(offsets.total);
    }
  }, [activeIndex, recording.segments.length, offsets, loadSegment]);

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium tabular-nums">
            {formatClock(recording.startedAt, timezone)} – {formatClock(recording.endedAt, timezone)}
          </span>
          <span className="ml-2 text-2xs text-muted-foreground">
            {formatDuration(Math.round(recording.durationMs / 1000))} ·{' '}
            {(recording.totalBytes / 1024).toFixed(0)} KB · {recording.segmentCount} segment
            {recording.segmentCount === 1 ? '' : 's'}
          </span>
        </div>
        {recording.segments[0] && recording.segments[0].lagSeconds > 180 ? (
          <span className="shrink-0 text-2xs text-track-backfill">
            arrived {formatLag(recording.segments[0].lagSeconds)}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlay}
          disabled={loadingSegment && !playing}
          aria-label={playing ? 'Pause' : 'Play'}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {playing ? (
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
              <path d="M8 5.5v13l11-6.5z" />
            </svg>
          )}
        </button>

        <Waveform
          peaks={recording.overviewPeaks}
          peakLevel={recording.peakLevel}
          progress={offsets.total > 0 ? elapsedMs / offsets.total : 0}
          onSeek={(fraction) => seek(fraction * offsets.total)}
        />

        <span className="shrink-0 text-2xs tabular-nums text-muted-foreground">
          {formatMs(elapsedMs)} / {formatMs(offsets.total)}
        </span>
      </div>

      {error ? <p className="mt-2 text-2xs text-danger">{error}</p> : null}

      {/* Custom transport above is the control surface; the element itself is
          hidden and carries no caption track (this is recorded voice, not media
          with subtitles). */}
      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        className="hidden"
      />
    </div>
  );
}

function indexFor(ms: number, starts: number[]): number {
  let index = 0;
  for (let i = 0; i < starts.length; i++) {
    if (ms >= (starts[i] ?? 0)) index = i;
    else break;
  }
  return index;
}

/**
 * The waveform, drawn on a canvas.
 *
 * Voice spikes are the whole point of drawing this rather than a plain
 * progress bar: a supervisor scanning a seven-minute recording wants to jump to
 * where someone was actually talking, not listen to six minutes of a phone on a
 * desk. So the bars are coloured by amplitude — near-silence is a faint floor,
 * and anything above the voice threshold is drawn tall and in the accent, which
 * makes speech legible as a shape. Progress is a second axis on the same bars:
 * the played region is saturated, the rest muted, with a crisp playhead between.
 *
 * Redrawn on every position change (480 bars is nothing) and on theme change,
 * because the colours come from CSS variables that flip with the theme.
 */
function Waveform({
  peaks,
  peakLevel,
  progress,
  onSeek,
}: {
  peaks: number[];
  peakLevel: number;
  progress: number;
  onSeek: (fraction: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // The amplitude at which a bar reads as "voice" rather than room noise.
  // Relative to the loudest sample in this recording, so a quiet clip is not
  // one flat grey line and a loud one is not all accent.
  const voiceThreshold = Math.max(40, peakLevel * 0.45);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;
    const width = parent.clientWidth;
    const height = 44;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const styles = getComputedStyle(canvas);
    const hsl = (name: string, alpha = 1) => {
      const triple = styles.getPropertyValue(name).trim() || '215 16% 47%';
      return `hsl(${triple} / ${alpha})`;
    };
    const played = hsl('--track-moving');
    const playedSpike = hsl('--track-backfill');
    const idle = hsl('--track-idle', 0.4);
    const idleSpike = hsl('--track-backfill', 0.45);

    const n = peaks.length;
    if (n === 0) return;
    const barGap = 1;
    const barWidth = Math.max(1, width / n - barGap);
    const mid = height / 2;
    const playedX = progress * width;

    for (let i = 0; i < n; i++) {
      const value = peaks[i] ?? 0;
      const x = (i / n) * width;
      const h = Math.max(2, (value / 255) * (height - 4));
      const isVoice = value >= voiceThreshold;
      const isPlayed = x <= playedX;

      ctx.fillStyle = isPlayed
        ? isVoice
          ? playedSpike
          : played
        : isVoice
          ? idleSpike
          : idle;
      ctx.fillRect(x, mid - h / 2, barWidth, h);
    }

    // Playhead.
    if (progress > 0 && progress < 1) {
      ctx.fillStyle = hsl('--foreground', 0.75);
      ctx.fillRect(playedX - 0.5, 0, 1, height);
    }
  }, [peaks, progress, voiceThreshold]);

  useEffect(() => {
    draw();
    const observer = new ResizeObserver(() => draw());
    const parent = canvasRef.current?.parentElement;
    if (parent) observer.observe(parent);
    return () => observer.disconnect();
  }, [draw]);

  return (
    <div
      className="relative h-11 min-w-0 flex-1 cursor-pointer"
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        onSeek(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
      }}
      role="slider"
      aria-label="Seek recording"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') onSeek(Math.max(0, progress - 0.05));
        if (e.key === 'ArrowRight') onSeek(Math.min(1, progress + 0.05));
      }}
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
